import { legacyHostName } from '../utils/legacyHostCompat';
export type RuntimeMode =
  | 'signed-host-app'
  | 'signed-login-browser'
  | 'ios-native-app'
  | 'chrome-browser'
  | 'safari-browser'
  | 'unknown-browser';

export interface RuntimeCapabilities {
  mode: RuntimeMode;
  canUseSignedHostAuth: boolean;
  canUseBrowserGeolocation: boolean;
  canUseClipboard: boolean;
  canOpenExternalMaps: boolean;
  canUseNativeHostBack: boolean;
  canUseHostCloudStorage: boolean;
}

export interface OpenExternalUrlOptions {
  tryHostBridge?: boolean;
  target?: string;
  features?: string;
}

export interface OpenExternalUrlResult {
  ok: boolean;
  url: string;
  mode: RuntimeMode;
  fallbackReason?: string;
}

export interface RuntimeSupportProfile {
  modeLabel: string;
  supportPercent: number;
  supportedGroups: readonly string[];
  signedOnlyGroups: readonly string[];
  demoCopy: string;
}

export const runtimeSupportMatrix = {
  signedHost: {
    modeLabel: 'Signed App Session',
    supportPercent: 100,
    supportedGroups: [
      'Public search and maps',
      'Google Maps navigation handoff',
      'Favorites and worklists',
      'Hazards, weather, and field intelligence',
      'Route preview',
      'Signed company records',
      'Forms, JSAs, and timesheets',
      'Fleet, policies, and training',
      'Workflow notifications and meetings',
      'AI/RAG company knowledge',
    ],
    signedOnlyGroups: [],
    demoCopy: 'Full signed app experience with private records, company workflows, notifications, and AI.',
  },
  browserDemo: {
    modeLabel: 'Chrome Free Demo Mode',
    supportPercent: 50,
    supportedGroups: [
      'Public search and maps',
      'Google Maps navigation handoff',
      'Favorites and worklists',
      'Hazards, weather, and field intelligence',
      'Route preview',
    ],
    signedOnlyGroups: [
      'Signed company records',
      'Forms, JSAs, and timesheets',
      'Fleet, policies, and training',
      'Workflow notifications and meetings',
      'AI/RAG company knowledge',
    ],
    demoCopy: 'Free browser preview for public navigation workflows. Use the native app or a company account for signed company features.',
  },
  iosNativeApp: {
    modeLabel: 'ZiaWellNav iOS App',
    supportPercent: 100,
    supportedGroups: [
      'Public search and maps',
      'Google Maps navigation handoff',
      'Favorites and worklists',
      'Hazards, weather, and field intelligence',
      'Route preview',
      'Signed company records',
      'Forms, JSAs, and timesheets',
      'Fleet, policies, and training',
      'Workflow notifications and meetings',
      'AI/RAG company knowledge',
    ],
    signedOnlyGroups: [],
    demoCopy: 'Native iOS app experience with ZiaWellNav field workflows available in-app.',
  },
} as const satisfies Record<string, RuntimeSupportProfile>;

const getHostBridgeWebApp = (): any | null => {
  if (typeof window === 'undefined') return null;
  const hostGlobal = legacyHostName();
  return (window as any)[hostGlobal]?.WebApp || null;
};

const getLaunchParamInitData = (): string => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams();
  const append = (raw: string) => {
    const normalized = raw.replace(/^[?#]/, '');
    if (!normalized) return;
    try {
      new URLSearchParams(normalized).forEach((value, key) => {
        if (!params.has(key)) params.set(key, value);
      });
    } catch {
      // Ignore malformed launch params.
    }
  };
  append(window.location.search);
  append(window.location.hash);
  return params.get('tgWebAppData') || '';
};

const hasLegacyLoginSession = (): boolean => {
  try {
    return Boolean(window.sessionStorage?.getItem('tg_auth_user'));
  } catch {
    return false;
  }
};

const getCapacitorPlatform = (): string => {
  if (typeof window === 'undefined') return '';
  const capacitor = (window as any).Capacitor;
  try {
    return String(capacitor?.getPlatform?.() || capacitor?.platform || '').toLowerCase();
  } catch {
    return String(capacitor?.platform || '').toLowerCase();
  }
};

const isNativeLocalhostProtocol = (): boolean => {
  if (typeof window === 'undefined') return false;
  const { protocol, hostname } = window.location;
  return (protocol === 'capacitor:' || protocol === 'ionic:') && hostname === 'localhost';
};

export const isIosNativeAppRuntime = (): boolean => getCapacitorPlatform() === 'ios' || isNativeLocalhostProtocol();

/** Keep native-only shortcuts out of Safari, Chrome, and installed web apps. */
export const isNativeMobileAppRuntime = (): boolean =>
  ['ios', 'android'].includes(getCapacitorPlatform()) || isNativeLocalhostProtocol();

export const getRuntimeMode = (): RuntimeMode => {
  if (typeof window === 'undefined') return 'unknown-browser';

  const webApp = getHostBridgeWebApp();
  const initData = String(webApp?.initData || getLaunchParamInitData() || '');
  if (initData) return 'signed-host-app';
  if (hasLegacyLoginSession()) return 'signed-login-browser';
  if (isIosNativeAppRuntime()) return 'ios-native-app';

  const userAgent = navigator.userAgent || '';
  const isChrome = /Chrome|CriOS|Chromium/i.test(userAgent) && !/Edg|OPR|SamsungBrowser/i.test(userAgent);
  if (isChrome) return 'chrome-browser';

  const isSafari = /Safari/i.test(userAgent) && !/Chrome|CriOS|Chromium|Android|Edg|OPR/i.test(userAgent);
  if (isSafari) return 'safari-browser';

  return 'unknown-browser';
};

export const getRuntimeCapabilities = (): RuntimeCapabilities => {
  const mode = getRuntimeMode();
  const webApp = getHostBridgeWebApp();
  const hasSignedHostAuth = mode === 'signed-host-app';

  return {
    mode,
    canUseSignedHostAuth: hasSignedHostAuth,
    canUseBrowserGeolocation: typeof navigator !== 'undefined' && Boolean(navigator.geolocation),
    canUseClipboard: typeof navigator !== 'undefined' && Boolean(navigator.clipboard?.writeText),
    canOpenExternalMaps:
      typeof window !== 'undefined' &&
      (typeof window.open === 'function' || typeof webApp?.openLink === 'function'),
    canUseNativeHostBack: hasSignedHostAuth && typeof webApp?.BackButton?.show === 'function',
    canUseHostCloudStorage: hasSignedHostAuth && Boolean(webApp?.CloudStorage),
  };
};

export const getRuntimeSupportProfile = (mode: RuntimeMode): RuntimeSupportProfile => {
  if (mode === 'signed-host-app') return runtimeSupportMatrix.signedHost;
  if (mode === 'ios-native-app') return runtimeSupportMatrix.iosNativeApp;
  return runtimeSupportMatrix.browserDemo;
};

/** Opens this app's iOS Settings page so Microphone / Speech Recognition can be turned back on. */
export function openSystemAppSettings(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const result = openExternalUrl('app-settings:', { tryHostBridge: false });
    if (result.ok) return true;
  } catch {
    // Fall through to a direct location assign.
  }
  try {
    window.location.href = 'app-settings:';
    return true;
  } catch {
    return false;
  }
}

export const openExternalUrl = (
  url: string,
  options: OpenExternalUrlOptions = {}
): OpenExternalUrlResult => {
  const capabilities = getRuntimeCapabilities();
  const webApp = getHostBridgeWebApp();
  const target = options.target || '_blank';
  const features = options.features || 'noopener,noreferrer';

  if (options.tryHostBridge !== false && capabilities.canUseSignedHostAuth && typeof webApp?.openLink === 'function') {
    try {
      webApp.openLink(url, { try_instant_view: false });
      return { ok: true, url, mode: capabilities.mode };
    } catch (error) {
      console.warn('[Runtime] Host openLink failed, falling back to browser open', error);
    }
  }

  try {
    const opened = window.open(url, target, features);
    // A null return is NOT a failure: per spec, window.open returns null whenever
    // 'noopener' is present in the features string, and the native iOS Capacitor
    // shell's WKWebView delegation handler opens the URL externally via
    // UIApplication.open and also returns nil. Only a thrown exception means the
    // link could not be opened.
    if (opened) {
      try { opened.opener = null; } catch {}
    }
    return { ok: true, url, mode: capabilities.mode };
  } catch (error) {
    return {
      ok: false,
      url,
      mode: capabilities.mode,
      fallbackReason: error instanceof Error ? error.message : 'Unable to open link',
    };
  }
};
